/**
 * ImportEntryPage.jsx — v4
 * FIXES:
 *   - Full success: shows a clear success screen for 2 seconds then navigates back
 *   - Partial success: marks successfully imported rows as done, removes them from
 *     preview so user cannot re-import them; only failed rows remain
 *   - Prevents accidental duplicate imports
 */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, FileSpreadsheet, Upload, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { createTransaction, getHeads } from '../../api/yea.api';
import { getFY } from '../../api/yea.api';
import { getSociety } from '../../api/society.api';
import { downloadExcelTemplate, parseExcelImport } from '../../utils/exportUtils';
import Logo   from '../../components/common/Logo';
import Button from '../../components/common/Button';
import './AddEntryPage.css';

const ImportEntryPage = () => {
  const { societyId, fyId, type } = useParams();
  const navigate  = useNavigate();
  const fileRef   = useRef(null);
  const label     = type === 'income' ? 'Income' : 'Expense';

  const [fy,              setFy]              = useState(null);
  const [society,         setSociety]         = useState(null);
  const [heads,           setHeads]           = useState([]);
  const [banks,           setBanks]           = useState([]);

  const [step,            setStep]            = useState(2); // Upload always accessible; step 1 is optional
  const [templateLoading, setTemplateLoading] = useState(false);
  const [parseLoading,    setParseLoading]    = useState(false);
  const [importLoading,   setImportLoading]   = useState(false);

  const [importPreview,   setImportPreview]   = useState([]);
  const [importErrors,    setImportErrors]    = useState([]);
  const [failedRows,      setFailedRows]      = useState([]);
  const [fileName,        setFileName]        = useState('');

  // Success state — shown after a fully successful import before navigating away
  const [importSuccess,   setImportSuccess]   = useState(null); // { count }
  const [alreadyImported, setAlreadyImported] = useState(false); // true after successful import of current file

  const backUrl = `/dashboard/society/${societyId}/fy/${fyId}?module=yea&tab=${type}`;
  const goBack  = () => navigate(backUrl);

  useEffect(() => {
    Promise.all([
      getFY(societyId, fyId),
      getSociety(societyId).catch(() => null),
      getHeads(societyId, type),
    ]).then(([fyRes, socRes, headRes]) => {
      const loadedFY = fyRes.data.fy;
      setFy(loadedFY);
      setSociety(socRes?.data?.society || null);
      setHeads(headRes.data.heads || []);
      setBanks(loadedFY?.bankOpeningBalances || []);
    }).catch(() => toast.error('Failed to load data'));
  }, [societyId, fyId, type]);

  const handleDownloadTemplate = async () => {
    if (heads.length === 0) {
      toast.error('Add heads in Account Heads tab first');
      return;
    }
    try {
      setTemplateLoading(true);
      await downloadExcelTemplate(heads, type, fy?.fyName || 'FY');
      toast.success('Template downloaded');
      setStep(2);
    } catch {
      toast.error('Failed to generate template');
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload .xlsx file');
      return;
    }
    setFileName(file.name);
    setParseLoading(true);
    setImportErrors([]);
    setImportPreview([]);
    setFailedRows([]);
    setImportSuccess(null);
    setAlreadyImported(false);
    try {
      const latestHeads = (await getHeads(societyId, type)).data.heads;
      const { rows, errors } = await parseExcelImport(file, latestHeads, banks);
      setImportErrors(errors);
      setImportPreview(rows);
      if (rows.length > 0) setStep(3);
    } catch {
      toast.error('Failed to read file');
    } finally {
      setParseLoading(false);
    }
    e.target.value = '';
  };

  const removePreviewRow = (idx) => {
    setImportPreview(prev => prev.filter((_, i) => i !== idx));
  };

  const handleImportConfirm = async () => {
    if (!importPreview.length) return;
    setImportLoading(true);
    setFailedRows([]);

    const results = await Promise.allSettled(
      importPreview.map((row, i) =>
        createTransaction(societyId, fyId, { ...row, type })
          .then(() => ({ status: 'ok', idx: i }))
          .catch(err => ({
            status: 'fail',
            idx: i,
            rowNum: i + 1,
            date: row.date,
            receiptNo: row.receiptNo || '—',
            reason: err.response?.data?.message || 'Unknown error',
          }))
      )
    );

    const allResults = results.map(r => r.value);
    const failed     = allResults.filter(r => r.status === 'fail');
    const okCount    = allResults.filter(r => r.status === 'ok').length;
    const failedIdxs = new Set(failed.map(r => r.idx));

    setImportLoading(false);

    if (failed.length === 0) {
      // Full success — show success screen then navigate back
      setImportPreview([]);
      setAlreadyImported(true);
      setImportSuccess({ count: okCount });
      setTimeout(() => navigate(backUrl), 2500);
    } else if (okCount > 0) {
      // Partial success — remove successfully imported rows from preview
      // so user cannot accidentally re-import them
      setImportPreview(prev => prev.filter((_, i) => failedIdxs.has(i)));
      setFailedRows(failed.map((f, i) => ({ ...f, rowNum: i + 1 })));
      toast.error(`${okCount} imported, ${failed.length} failed — see details below`);
    } else {
      // All failed
      setFailedRows(failed);
      toast.error('All entries failed to import');
    }
  };

  // ── Full success screen ───────────────────────────────────────────────────
  if (importSuccess) {
    return (
      <div className="aep-root">
        <div className="aep-topbar">
          <button className="aep-back" onClick={goBack}>
            <ArrowLeft size={14} /> Back
          </button>
          <Logo size="sm" />
          <div className="aep-topbar-title">
            <span className="aep-society">{society?.name || ''}</span>
            <span className="aep-badge">Import {label} Entries</span>
            {fy && <span className="aep-fy">{fy.fyName}</span>}
          </div>
        </div>
        <div className="aep-body">
          <div className="aep-form-wrap" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', gap: '1.25rem' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--success-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle size={36} color='var(--success)' strokeWidth={1.5} />
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.4rem' }}>
                Import Successful
              </div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                {importSuccess.count} {label.toLowerCase()} {importSuccess.count === 1 ? 'entry' : 'entries'} imported successfully.
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '0.5rem' }}>
                Returning to {label} entries…
              </div>
            </div>
            <Button onClick={goBack}>Go Back Now</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aep-root">

      {/* Top bar */}
      <div className="aep-topbar">
        <button className="aep-back" onClick={goBack}>
          <ArrowLeft size={14} /> Back
        </button>
        <Logo size="sm" />
        <div className="aep-topbar-title">
          <span className="aep-society">{society?.name || ''}</span>
          <span className="aep-badge">Import {label} Entries</span>
          {fy && <span className="aep-fy">{fy.fyName}</span>}
        </div>
      </div>

      <div className="aep-body">
        <div className="aep-form-wrap">

          {/* Step indicator */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', alignItems: 'center' }}>
            {[
              { n: 1, label: 'Download template' },
              { n: 2, label: 'Upload filled file' },
              { n: 3, label: 'Review & confirm'  },
            ].map((s, i, arr) => (
              <React.Fragment key={s.n}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: step >= s.n ? 1 : 0.4 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: step > s.n ? 'var(--success)' : step === s.n ? 'var(--tc)' : 'var(--bg-overlay)',
                    color: step >= s.n ? '#fff' : 'var(--text-muted)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.8rem', fontWeight: 700, flexShrink: 0,
                  }}>
                    {step > s.n ? <CheckCircle size={14} /> : s.n}
                  </div>
                  <span style={{ fontSize: '0.82rem', color: step === s.n ? 'var(--text)' : 'var(--text-muted)', fontWeight: step === s.n ? 600 : 400 }}>
                    {s.label}
                  </span>
                </div>
                {i < arr.length - 1 && (
                  <div style={{ flex: 1, height: 1, background: 'var(--border-subtle)', maxWidth: 48 }} />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1 — Download template */}
          <div className="aep-section">
            <div className="aep-section-label">Step 1 — Download template</div>
            <div style={{ padding: '1.25rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                Download the Excel template pre-filled with your {label.toLowerCase()} heads for {fy?.fyName}.
                Fill in your data and save the file.
              </p>
              <Button size="sm" variant="ghost" icon={FileSpreadsheet} loading={templateLoading} onClick={handleDownloadTemplate}>
                Download {label} Template
              </Button>
              {step > 1 && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <CheckCircle size={13} /> Template downloaded
                </div>
              )}
            </div>
          </div>

          {/* Step 2 — Upload file */}
          <div className="aep-section">
            <div className="aep-section-label">Step 2 — Upload filled template</div>
            <div style={{ padding: '1.25rem', background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                Upload your filled .xlsx file. The system will validate and preview entries before importing.
              </p>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.1rem', background: 'var(--bg-overlay)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--text)', fontWeight: 500, transition: 'border-color 0.15s' }}>
                <Upload size={15} />
                {parseLoading ? 'Reading file...' : fileName ? `Change file (${fileName})` : 'Choose .xlsx file'}
                <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileSelect} />
              </label>

              {importErrors.length > 0 && (
                <div style={{ marginTop: '1rem', padding: '0.85rem', background: 'var(--danger-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(240,107,107,0.3)' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--danger)', marginBottom: '0.4rem' }}>
                    {importErrors.length} error{importErrors.length !== 1 ? 's' : ''} found:
                  </div>
                  {importErrors.map((e, i) => (
                    <div key={i} style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>• {e}</div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Step 3 — Preview */}
          {step === 3 && importPreview.length > 0 && (
            <div className="aep-section">
              <div className="aep-section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Step 3 — Review entries ({importPreview.length})</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400 }}>
                  Remove any rows you don't want to import
                </span>
              </div>
              <div style={{ background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
                <table className="ledger-table" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Month</th>
                      <th>Receipt No</th>
                      <th>Head(s)</th>
                      <th style={{ textAlign: 'right' }}>Amount (₹)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{idx + 1}</td>
                        <td style={{ fontSize: '0.82rem' }}>{row.date}</td>
                        <td><span className="badge badge-subtle">{row.month}</span></td>
                        <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{row.receiptNo || '—'}</td>
                        <td style={{ fontSize: '0.78rem' }}>
                          {(row.headEntries || []).map(h => h.headName).join(', ')}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          {Number(row.totalAmount).toLocaleString('en-IN')}
                        </td>
                        <td>
                          <button onClick={() => removePreviewRow(idx)}
                            style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: '4px' }}>
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td colSpan={5} style={{ textAlign: 'right', fontWeight: 600, fontSize: '0.78rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                        Total ({importPreview.length} entries)
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--success)', fontVariantNumeric: 'tabular-nums' }}>
                        {importPreview.reduce((s, r) => s + Number(r.totalAmount), 0).toLocaleString('en-IN')}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Failed rows panel */}
          {failedRows.length > 0 && (
            <div className="aep-section">
              <div className="aep-section-label" style={{ color: 'var(--danger)' }}>
                {failedRows.length} row{failedRows.length !== 1 ? 's' : ''} failed to import
              </div>
              <div style={{ padding: '0.85rem', background: 'var(--danger-bg)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(240,107,107,0.3)' }}>
                {failedRows.map((f, i) => (
                  <div key={i} style={{ fontSize: '0.8rem', color: 'var(--danger)', marginBottom: '0.3rem', display: 'flex', gap: '0.5rem' }}>
                    <span style={{ fontWeight: 600, flexShrink: 0 }}>
                      Row {f.rowNum} ({f.date}{f.receiptNo !== '—' ? `, Rcpt: ${f.receiptNo}` : ''}):
                    </span>
                    <span>{f.reason}</span>
                  </div>
                ))}
                <div style={{ marginTop: '0.75rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Fix these entries manually via <strong>Add {label} Entry</strong> or correct the Excel and re-upload.
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Sticky footer */}
      <div className="aep-footer">
        <div className="aep-validation">
          {step === 3 && importPreview.length > 0 && failedRows.length === 0 && !alreadyImported && (
            <span className="aep-valid">
              <CheckCircle size={15} /> {importPreview.length} entries ready to import
            </span>
          )}
          {alreadyImported && (
            <span style={{ fontSize: '0.82rem', color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <CheckCircle size={15} /> Already imported — choose a new file to import again
            </span>
          )}
          {failedRows.length > 0 && importPreview.length === 0 && (
            <span className="aep-invalid">
              <XCircle size={15} /> {failedRows.length} entries failed — correct and re-upload
            </span>
          )}
          {failedRows.length > 0 && importPreview.length > 0 && (
            <span className="aep-invalid">
              <XCircle size={15} /> {failedRows.length} entries failed — {importPreview.length} remaining to retry
            </span>
          )}
          {step < 3 && failedRows.length === 0 && (
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Complete all steps to import entries
            </span>
          )}
        </div>
        <div className="aep-actions">
          <Button variant="subtle" onClick={goBack}>
            {failedRows.length > 0 ? 'Done' : 'Cancel'}
          </Button>
          {step === 3 && importPreview.length > 0 && (
            <Button loading={importLoading} disabled={alreadyImported} onClick={handleImportConfirm}>
              Import {importPreview.length} {label} Entries
            </Button>
          )}
        </div>
      </div>

    </div>
  );
};

export default ImportEntryPage;
